import React, { useState } from "react";
import { Loader2, Inbox } from "lucide-react";
import { fmtMoney, fmtCompact, axisDenom, denomTick, CCY_SYMBOL } from "@/lib/moneyFormat";

export { fmtMoney, fmtCompact, axisDenom, denomTick, CCY_SYMBOL };

export const BRAND_LOGO = "/barjeel-logo.jpeg";

// Shared Recharts theme ----------------------------------------------------
export const CHART_COLORS = ["#064E3B", "#C59B27", "#0B192C", "#10B981", "#3B82F6", "#8B5CF6", "#F59E0B"];

export const chartTooltipStyle = {
  backgroundColor: "rgba(11, 25, 44, 0.96)",
  border: "1px solid rgba(197, 155, 39, 0.4)",
  borderRadius: "0.75rem",
  boxShadow: "0 10px 25px -5px rgba(0,0,0,0.35)",
  color: "#FFFFFF",
  padding: "10px 14px",
  fontSize: "12px",
  fontFamily: "Plus Jakarta Sans",
};
export const chartGrid = { strokeDasharray: "3 3", stroke: "rgba(148,163,184,0.22)" };

// Compact by default; exact amount via hover (title) on desktop and tap-to-toggle on mobile.
export function Money({ value, ccy = "USD", className = "", compact = true }) {
  const [full, setFull] = useState(false);
  const exact = fmtMoney(value, ccy);
  if (!compact) return <span className={`font-num ${className}`}>{exact}</span>;
  return (
    <span
      className={`font-num ${className}`}
      title={exact}
      style={{ cursor: "help" }}
      onClick={(e) => { e.stopPropagation(); setFull((f) => !f); }}
    >
      {full ? exact : fmtCompact(value, ccy)}
    </span>
  );
}

export function PageHeader({ overline, title, subtitle, actions, testid, wm = "pattern" }) {
  return (
    <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6" data-testid={testid}>
      <Watermark variant={wm} />
      <div>
        {overline && <div className="overline-label text-gold mb-1.5">{overline}</div>}
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-50">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl leading-relaxed">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function MockBadge({ label = "MOCK MODE", className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800/60 ${className}`}>
      {label}
    </span>
  );
}

// Subtle, unobtrusive synthetic-data chip
export function DemoChip({ className = "" }) {
  return (
    <span data-testid="demo-data-chip" className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-num uppercase tracking-[0.15em] text-muted-foreground/80 bg-muted/60 border border-border/60 ${className}`}>
      Synthetic demo data
    </span>
  );
}

const STATUS_COLORS = {
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800",
  clear: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800",
  live: "bg-emerald-50 text-emerald-800 border-emerald-200",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800",
  reconciled: "bg-emerald-50 text-emerald-800 border-emerald-200",
  verified: "bg-emerald-50 text-emerald-800 border-emerald-200",
  accepted: "bg-emerald-50 text-emerald-800 border-emerald-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800",
  pending_approval: "bg-amber-50 text-amber-800 border-amber-200",
  in_compliance_review: "bg-amber-50 text-amber-800 border-amber-200",
  information_requested: "bg-amber-50 text-amber-800 border-amber-200",
  potential_match: "bg-amber-50 text-amber-800 border-amber-200",
  potential_issue: "bg-amber-50 text-amber-800 border-amber-200",
  uncertain: "bg-amber-50 text-amber-800 border-amber-200",
  draft: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300",
  not_started: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300",
  open: "bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950/50 dark:text-blue-300",
  in_progress: "bg-blue-50 text-blue-800 border-blue-200",
  escalated: "bg-purple-50 text-purple-800 border-purple-200 dark:bg-purple-950/50 dark:text-purple-300",
  rejected: "bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950/50 dark:text-rose-300",
  failed: "bg-rose-50 text-rose-800 border-rose-200",
  exceptions: "bg-rose-50 text-rose-800 border-rose-200",
  unavailable: "bg-slate-200 text-slate-600 border-slate-300",
  closed: "bg-slate-200 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-400",
};

export function StatusBadge({ status, testid }) {
  const key = (status || "").toLowerCase();
  const cls = STATUS_COLORS[key] || "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300";
  return (
    <span data-testid={testid} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border capitalize ${cls}`}>
      {(status || "—").replace(/_/g, " ")}
    </span>
  );
}

export function Spinner({ label }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin text-primary" /> {label || "Loading…"}
    </div>
  );
}

export function EmptyState({ title, hint, icon: Icon = Inbox }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center rounded-xl border border-dashed border-border bg-muted/20">
      <div className="w-12 h-12 rounded-full bg-emerald-50 dark:bg-emerald-950/50 flex items-center justify-center text-primary mb-3">
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-medium text-slate-700 dark:text-slate-200">{title}</p>
      {hint && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{hint}</p>}
    </div>
  );
}

export function RiskBadge({ rating }) {
  const map = {
    Low: "bg-emerald-50 text-emerald-800 border-emerald-200",
    Medium: "bg-amber-50 text-amber-800 border-amber-200",
    High: "bg-rose-50 text-rose-800 border-rose-200",
  };
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold border ${map[rating] || "bg-slate-100 text-slate-700 border-slate-300"}`}>{rating || "Unrated"}</span>;
}

// KPI card with brand accent line
export function KpiCard({ label, children, sub, testid, className = "" }) {
  return (
    <div data-testid={testid} className={`relative overflow-hidden rounded-xl border border-border/80 bg-card p-4 sm:p-6 card-elev accent-line ${className}`}>
      <div className="overline-label text-muted-foreground mb-1.5">{label}</div>
      <div className="font-bold font-num tracking-tight text-foreground leading-tight break-words text-[clamp(1.15rem,4.5vw,1.875rem)]">{children}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1.5">{sub}</div>}
    </div>
  );
}

// Up/down trend pill
export function TrendPill({ value, pct, ccy = "USD", label = "30d", testid }) {
  if (value === null || value === undefined) return null;
  const up = value >= 0;
  return (
    <span data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-num font-semibold ${up ? "bg-emerald-500/15 text-emerald-200" : "bg-rose-500/15 text-rose-200"}`}>
      {up ? "▲" : "▼"} {pct !== null && pct !== undefined ? `${up ? "+" : ""}${pct}%` : ""} {`${up ? "+" : "−"}${fmtCompact(Math.abs(value), ccy)}`} <span className="opacity-70">· {label}</span>
    </span>
  );
}

// Decorative Dubai-inspired watermark. Purely ornamental: never captures clicks,
// hidden from assistive tech, locally served, and rendered at watermark opacity.
export function Watermark({ variant = "pattern", className = "" }) {
  const src = variant === "skyline" ? "/assets/skyline-watermark.jpg" : "/assets/emirati-pattern.jpg";
  return (
    <div aria-hidden="true" className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}>
      <div
        className="absolute -top-8 -right-8 w-[380px] h-[380px] bg-contain bg-no-repeat bg-top-right opacity-[0.05] mix-blend-multiply dark:mix-blend-screen dark:invert dark:opacity-[0.06]"
        style={{ backgroundImage: `url(${src})` }}
      />
    </div>
  );
}

// Shared high-contrast chart tooltip: deep navy surface, white labels & values,
// series colours overridden, kept inside the viewport (escape viewBox allowed).
export function ChartTooltip({ active, payload, label, ccy = "USD", money = true }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="max-w-[240px] rounded-xl border border-[#C59B27]/40 bg-[#0B192C]/95 px-3.5 py-2.5 shadow-xl">
      {label !== undefined && label !== null && label !== "" && (
        <div className="text-white text-xs font-bold mb-1 break-words">{String(label)}</div>
      )}
      {payload.map((p, i) => (
        <div key={i} className="text-white text-xs font-num break-words">
          {p.name && String(p.name) !== "value" ? `${p.name}: ` : ""}{money ? fmtMoney(p.value, ccy) : p.value}
        </div>
      ))}
    </div>
  );
}
export const tooltipChrome = { wrapperStyle: { zIndex: 60, outline: "none" }, allowEscapeViewBox: { x: true, y: true } };
